import axios from "axios";
import { apiCallBegan, apiCallFailed, apiCallSuccess } from "../api";
import { getApiErrorToast, getToast } from "../../../services/appAlerts";

//const API_BASE_URL="https://localhost:44334/api/" 
const API_BASE_URL = process?.env?.REACT_APP_API_BASEURL + "/api/";
const pendingGetRequests = new Map();
const cachedGetRequests = new Map();
const cancelTokens = new Map();
const DEFAULT_CACHE_DURATION = 60000;
const isDevelopment = process?.env?.NODE_ENV === "development";

const getRequestKey = ({ baseURL, url, method, data }) => {
  const requestMethod = (method || "get").toLowerCase();
  if (requestMethod !== "get") return null;

  return JSON.stringify({
    baseURL,
    url: String(url || "").toLowerCase(),
    data: data || null
  });
};

const getErrorPayload = (error) => ({
  message:
    error?.response?.data?.message ||
    error?.response?.data ||
    error?.message ||
    "Request failed",
  status: error?.response?.status,
  data: error?.response?.data,
  url: error?.config?.url
});

const logRequest = (label, url, startedAt) => {
  if (!isDevelopment) return;
  console.log(`[api] ${label}: ${url} (${Date.now() - startedAt}ms)`);
};

const toAction = (actionConfig, fallbackPayload, fallbackKey = "payload") => {
  if (!actionConfig) return null;
  if (typeof actionConfig === "string") return { type: actionConfig, payload: fallbackPayload };

  const hasConfiguredPayload = Object.prototype.hasOwnProperty.call(actionConfig, "payload");
  const payload = hasConfiguredPayload ? actionConfig.payload : fallbackPayload;

  return {
    ...actionConfig,
    payload:
      hasConfiguredPayload &&
      fallbackPayload !== undefined &&
      payload &&
      typeof payload === "object" &&
      !Array.isArray(payload)
        ? { ...payload, [fallbackKey]: fallbackPayload }
        : payload
  };
};

const api =
  ({ getState, dispatch }) =>
    (next) =>
      async (action) => {
        const token = localStorage.getItem("token");

        if (action.type !== apiCallBegan.type) return next(action);

        const {
          url,
          method,
          data,
          params,
          headers: requestHeaders,
          onStart,
          onSuccess,
          onError,
          withCredentials,
          responseType,
          baseURL,
          throwOnError,
          dedupe,
          cache,
          cacheDuration,
          cacheKey,
          forceRefresh,
          cancelPrevious,
          cancelKey,
          silentError
        } =
          action.payload;

        const headers = {
          Authorization: `Bearer ${token}`,
          LoginType: localStorage.getItem("loginType"),
          Portal: "workbench",
          ...requestHeaders
        };

        const startAction = toAction(onStart);
        if (startAction) dispatch(startAction);
        let api_url = baseURL;
        if (!baseURL) api_url = API_BASE_URL;

        next(action);

        const startedAt = Date.now();
        const requestKey = cacheKey || getRequestKey({ baseURL: api_url, url, method, data: params || data });
        const requestMethod = (method || "get").toLowerCase();
        const shouldDedupe = requestMethod === "get" && dedupe !== false;
        const shouldCache = requestMethod === "get" && cache === true;
        const requestCacheDuration = cacheDuration || DEFAULT_CACHE_DURATION;
        const cached = shouldCache ? cachedGetRequests.get(requestKey) : null;

        if (!forceRefresh && cached && Date.now() - cached.timestamp < requestCacheDuration) {
          logRequest("cache", url, startedAt);
          const successAction = toAction(onSuccess, cached.data, "data");
          if (successAction) dispatch(successAction);
          return cached.data;
        }

        if (cancelPrevious) {
          const key = cancelKey || requestKey;
          const cancel = cancelTokens.get(key);
          if (cancel) cancel("Cancelled because a newer request was started.");
        }

        try {
          let result;

          if (shouldDedupe && requestKey && pendingGetRequests.has(requestKey)) {
            logRequest("dedupe", url, startedAt);
            result = await pendingGetRequests.get(requestKey);
          } else {
            const source = axios.CancelToken.source();
            if (cancelPrevious) cancelTokens.set(cancelKey || requestKey, source.cancel);

            const request = axios.request({
              baseURL: api_url,
              url,
              method,
              data,
              params,
              headers,
              withCredentials,
              responseType,
              cancelToken: source.token
            });

            if (shouldDedupe && requestKey) pendingGetRequests.set(requestKey, request);
            result = await request;
          }

          //console.log(result)

          dispatch(apiCallSuccess(result.data));
          if (shouldCache && requestKey) {
            cachedGetRequests.set(requestKey, { data: result.data, timestamp: Date.now() });
          }
          logRequest("success", url, startedAt);

          const successAction = toAction(onSuccess, result.data, "data");
          if (successAction) dispatch(successAction);
          return result.data;

        } catch (error) {
          if (axios.isCancel(error)) return undefined;

          const errorPayload = getErrorPayload(error);
          dispatch(apiCallFailed(errorPayload));

          if (!silentError) {
            if (errorPayload.status === 401) {
              localStorage.removeItem("token");
              localStorage.removeItem("currentUser");
              getToast(errorPayload.message || "Your session has expired. Please sign in again.", "error", { timer: 5000 });
              window.setTimeout(() => {
                window.location = "/login";
              }, 700);
            } else {
              getApiErrorToast(errorPayload);
            }
          }

          logRequest("error", url, startedAt);

          const errorAction = toAction(onError, errorPayload, "error");
          if (errorAction) dispatch(errorAction);
          if (throwOnError) throw error;
          return undefined;
        } finally {
          if (requestKey) pendingGetRequests.delete(requestKey);
          if (cancelPrevious) cancelTokens.delete(cancelKey || requestKey);
        }
      };

export default api;
